import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { IsfModel } from '@app/data-layer/models/isf';
import { InvoicesRepository } from '@app/data-layer/repositories';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { merge, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-manufacturer-details',
  templateUrl: './isf-ocr-quick-fix-manufacturer-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-manufacturer-details.component.scss'],
})
export class IsfOcrQuickFixManufacturerDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public manufacturerFields: ISectionFormField[];
  @Input() public invoiceId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public shipment: ShipmentModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public manufacturerDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public seller: EntityModel;

  private invoice: InvoiceModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private invoicesRepository: InvoicesRepository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private translateService: TranslateService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.manufacturerFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const manufacturerInvalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, enumEntityModelCode.MANUFACTURER, true);
    if (isNeedCheck && Object.keys(manufacturerInvalidFields).length > 0) {
      this.fh.markAsTouched(this.formGroup);
      this.openMissingFieldsModal(manufacturerInvalidFields);
      return;
    }

    this.updateOrCreateManufacturer()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.currentIsf = isf;
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public get manufacturerTitle(): string {
    const currentInvoiceNumber = this.shipment.invoices.findIndex((invoice) => invoice.id === this.invoiceId) + 1;
    const translatedManufacturerText = this.translateService.instant('ENTITY.MANUFACTURER');

    return this.shipment.invoices.length > 1 ? `${translatedManufacturerText} #${currentInvoiceNumber}` : translatedManufacturerText;
  }

  public get isSellerAssignedToOtherManufacturer(): boolean {
    const invoices = this.shipment.invoices;
    const manufacturerIds = invoices.map((invoice) => invoice.manufacturerId);
    const uniqueManufacturerIds = [...new Set(manufacturerIds)];

    return this.seller && this.invoice?.manufacturer?.id !== this.seller.id && uniqueManufacturerIds.includes(this.seller.id);
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const manufacturer = this.invoice?.manufacturer;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const manufacturerAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(manufacturer?.address);
    const nameField = this.manufacturerFields.find((field) => field.name === 'name');

    this.manufacturerDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: manufacturer?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: manufacturerAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const manufacturerAddressErrors = this.invoice?.manufacturer?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(manufacturerAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateManufacturer() {
    const manufacturer: EntityModel = this.formGroup.value;
    const manufacturerId = this.formGroup.get('id').value;

    let newInvoiceData: Partial<InvoiceModel>;

    if (manufacturerId === this.seller?.id) {
      newInvoiceData = {
        ...this.invoice,
        manufacturer: this.seller,
        manufacturerId: this.seller?.id,
      };
    } else {
      newInvoiceData = {
        ...this.invoice,
        manufacturer,
        manufacturerId,
      };
    }

    return of(manufacturer).pipe(
      switchMap((entity) => {
        if (!manufacturerId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.MANUFACTURER;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        const invoiceCreateUpdateData = {
          ...newInvoiceData,
          manufacturer: entity,
          manufacturerId: entity.id,
        };

        if (!this.invoiceId) {
          return this.createInvoice(invoiceCreateUpdateData);
        } else {
          return this.updateInvoice(invoiceCreateUpdateData);
        }
      }),
      switchMap((newInvoice) => {
        let invoices: InvoiceModel[] = [];

        if (this.shipment.invoices.length) {
          invoices = this.shipment.invoices.map((invoice) => {
            if (invoice.id === this.invoiceId) {
              return newInvoice;
            }

            return invoice;
          });
        } else {
          invoices = [newInvoice];
        }

        const updatedShipment = this.shipment;
        updatedShipment.invoices = invoices;

        return this.isfStateService.updateShipment(updatedShipment);
      }),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private createInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoice };
    }

    return this.invoicesRepository.create(this.shipment.id, newInvoiceData);
  }

  private updateInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoice };
    }

    return this.invoicesRepository.update(this.invoice.id, newInvoiceData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.MANUFACTURER);

    this.formGroup.patchValue(this.invoice?.manufacturer);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((manufacturerId) => {
        this.applySellerOrManufacturer(manufacturerId);
      });

    merge(this.formGroup.get('name').valueChanges, this.formGroup.get('address').valueChanges)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        if (!this.formGroup.pristine && this.seller && this.formGroup.get('id').value === this.seller.id) {
          this.formGroup.get('id').patchValue(this.invoice?.manufacturer?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private applySellerOrManufacturer(id: number) {
    if (this.seller && id === this.seller.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.seller);
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.invoice?.manufacturer);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.shipment = isf.shipment;
    this.invoice = this.isf.shipment.invoices.find((invoice) => invoice.id === this.invoiceId);
    this.seller = this.isf?.shipment?.seller;
  }
}
